import { ColumnsType } from 'antd/es/table';
import { Trans, useTranslation } from 'react-i18next';

import { EmptyDataWrap } from '@/components/EmptyDataWrap';
import PositionSizeDisplay from '@/components/PositionSizeDisplay';
import TokenPairSimple from '@/components/TokenPair/TokenPairSimple';
import LeanMoreToolTip from '@/components/ToolTip/LeanMoreToolTip';
import PnlTitleToolTip from '@/components/ToolTip/PnlTitleToolTip';
import { FAQ_LINKS } from '@/constants/links';
import { WrappedPosition } from '@/entities/WrappedPosition';
import { usePriceBasisForPnl } from '@/hooks/usePriceBasisForPnl';
import { bigNumberSort } from '@/utils/numberUtil';
import { useMemo } from 'react';
import AdjustMarginLink from '../TradePortfolio/TradePosition/AdjustMarginLink';
import ClosePositionLink from '../TradePortfolio/TradePosition/ClosePositionLink';
import SharePnlButton from '../TradePortfolio/TradePosition/SharePnlButton';

export const usePositionTableColumns = (): ColumnsType<WrappedPosition> => {
  const { t } = useTranslation();
  const { priceBasisForPnl } = usePriceBasisForPnl();

  return useMemo(
    () => [
      {
        title: t('common.pair'),
        dataIndex: 'pair',
        key: 'pair',
        width: 120,
        align: 'left',
        render: (_, position: WrappedPosition) => (
          <EmptyDataWrap isLoading={!position?.rootPair}>
            <TokenPairSimple
              tradeSide={position.side}
              leverage={position.leverage}
              isInverse={position?.rootPair.isInverse}
              baseToken={position?.rootPair?.rootInstrument.baseToken}
              quoteToken={position?.rootPair?.rootInstrument.quoteToken}
            />
          </EmptyDataWrap>
        ),
      },

      {
        title: t('common.size'),
        dataIndex: 'size',
        key: 'size',
        width: 100,
        align: 'left',
        render: (_, position: WrappedPosition) => <PositionSizeDisplay position={position} />,
      },
      {
        title: t('common.tradePortfolio.positionV'),
        dataIndex: 'positionValue',
        key: 'positionValue',
        width: 120,
        align: 'left',
        defaultSortOrder: 'descend',
        sorter: (a, b) => bigNumberSort(a.positionValue?.abs(), b.positionValue?.abs()),
        render: (_, position: WrappedPosition) => (
          <EmptyDataWrap isLoading={!position?.positionValue}>
            {position.positionValue.abs().formatNumberWithTooltip({
              suffix: position?.rootInstrument?.quoteToken?.symbol,
              isShowTBMK: true,
              showPositive: false,
            })}
          </EmptyDataWrap>
        ),
      },
      {
        title: t('common.tradePortfolio.entryP'),
        dataIndex: 'entryPrice',
        key: 'entryPrice',
        align: 'left',
        width: 90,
        render: (_, position: WrappedPosition) => (
          <EmptyDataWrap isLoading={!position?.entryPrice}>
            {position?.wrapAttribute('entryPrice').formatPriceNumberWithTooltip()}
          </EmptyDataWrap>
        ),
      },
      {
        title: t('common.tradePortfolio.marketP'),
        dataIndex: 'markPrice',
        key: 'markPrice',
        width: 90,
        align: 'left',
        render: (_, position: WrappedPosition) => (
          <EmptyDataWrap isLoading={!position?.rootPair?.markPrice}>
            {position?.rootPair?.wrapAttribute('markPrice').formatPriceNumberWithTooltip()}
          </EmptyDataWrap>
        ),
      },
      {
        title: <PnlTitleToolTip />,
        dataIndex: 'pnl',
        key: 'pnl',
        width: 150,
        align: 'left',
        render: (_, position: WrappedPosition) => {
          const pnl = position?.getUnrealizedPositionPnlWithoutFunding(priceBasisForPnl);
          const pnlPercentage = position?.getUnrealizedPnlPercentage(priceBasisForPnl);

          return position?.unrealizedSocialLoss.lte(0) ? (
            <div className="syn-trade-position-table-row">
              {position?.getUnrealizedPositionPnlWithoutFunding(priceBasisForPnl).formatNumberWithTooltip({
                suffix: position.rootInstrument.marginToken.symbol,
                isShowTBMK: true,
                colorShader: true,
                showPositive: true,
                colorSuffix: true,
                isShowApproximatelyEqualTo: false,
              })}
              {position?.equity.gt(0) &&
                position?.getUnrealizedPnlPercentage(priceBasisForPnl).formatPercentage({
                  decimals: 2,
                  suffix: ')',
                  prefix: '(',
                  isHiddenIfZero: true,
                })}
              <SharePnlButton position={position} />
            </div>
          ) : (
            <EmptyDataWrap isLoading={!pnl}>
              <div className="syn-trade-position-table-row">
                <span className={pnl?.gt(0) ? 'text-green' : pnl?.lt(0) ? 'text-red' : ''}>
                  {pnl?.formatNumberWithTooltip({
                    isShowTBMK: true,
                    showPositive: true,
                    suffix: position?.rootInstrument?.marginToken?.symbol,
                  })}
                </span>
                {pnlPercentage && (
                  <span className={pnlPercentage?.gt(0) ? 'text-green' : pnlPercentage?.lt(0) ? 'text-red' : ''}>
                    {pnlPercentage.formatPercentage({
                      decimals: 2,
                      requirePlusSymbol: true,
                      prefix: '(',
                      suffix: ')',
                    })}
                  </span>
                )}
                <SharePnlButton position={position} />
              </div>
            </EmptyDataWrap>
          );
        },
      },
      {
        title: t('common.tradePortfolio.liqP'),
        dataIndex: 'liquidationPrice',
        key: 'liquidationPrice',
        width: 100,
        align: 'left',
        render: (_, position: WrappedPosition) => (
          <EmptyDataWrap isLoading={!position?.liquidationPrice}>
            {position?.liquidationPrice.formatPriceNumberWithTooltip()}
          </EmptyDataWrap>
        ),
      },
      {
        title: t('common.margin'),
        dataIndex: 'margin',
        key: 'margin',
        width: 100,
        align: 'left',
        render: (_, position: WrappedPosition) => (
          <EmptyDataWrap isLoading={!position?.rootPair}>
            <span className="syn-trade-position-table-row">
              {position?.equity.formatNumberWithTooltip({
                isShowTBMK: true,
                suffix: position.rootPair.rootInstrument.marginToken.symbol,
              })}{' '}
              <AdjustMarginLink chainId={position.rootPair.chainId} pairSymbol={position.rootPair.symbol} />
            </span>
          </EmptyDataWrap>
        ),
      },
      {
        title: (
          <LeanMoreToolTip
            link={FAQ_LINKS.PREDICTED_FUNDING_RATE}
            overlayInnerStyle={{ width: 270 }}
            title={
              <Trans i18nKey={'common.tradePage.tradePosition.unRealizedFundingTooltip'} components={{ b: <b /> }} />
            }>
            {t('common.tradePortfolio.funding')}
          </LeanMoreToolTip>
        ),
        dataIndex: 'funding',
        key: 'funding',
        width: 100,
        align: 'left',
        render: (_, position: WrappedPosition) => (
          <EmptyDataWrap isLoading={!position?.liquidationPrice}>
            {position?.wrapAttribute('unrealizedFundingFee').formatNumberWithTooltip({
              suffix: position.rootInstrument.marginToken.symbol,
              isShowTBMK: true,
              colorShader: true,
              showPositive: true,
              colorSuffix: true,
              isShowApproximatelyEqualTo: false,
            })}
          </EmptyDataWrap>
        ),
      },
      {
        title: t('common.tradePortfolio.action'),
        dataIndex: 'action',
        key: 'action',
        width: 80,
        align: 'right',
        render: (_, position: WrappedPosition) => (
          <EmptyDataWrap isLoading={!position}>
            <div className="syn-trade-position-table-row right">
              <ClosePositionLink chainId={position.rootPair.chainId} pairSymbol={position.rootPair.symbol} />
            </div>
          </EmptyDataWrap>
        ),
      },
    ],
    [priceBasisForPnl, t],
  );
};
